import DefaultScreen from '@components/Global/DefaultScreenView';
import React, { useState, useMemo, useCallback, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import { View, Text, Pressable, Platform, Dimensions, StyleSheet, PanResponder, Alert } from 'react-native';
import { ScrollView, GestureHandlerRootView } from 'react-native-gesture-handler';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { selectUserContext, selectUserContextWeights, selectUserId, selectUserProfile, selectUserAvatar, selectUserEmail, selectUserName } from '@reduxLocal/selectors/userSelectors';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { Card, XStack, YStack, SizableText, Slider, H2, H3, Paragraph, Avatar, Button } from 'tamagui';
import * as Animatable from 'react-native-animatable';
import { MaterialIcons, FontAwesome5, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import styles, { getModernCardStyle, getModernTextColor } from '@stylesheet';
import AvatarHandler from '@components/Global/AvatarHandler/AvatarHandler';
import { ReactNativeZoomableView } from '@openspacelabs/react-native-zoomable-view';
import HeaderComponent from './Components/HeaderComponent';
import Animated, { 
  useSharedValue, 
  useAnimatedStyle,
  withRepeat, 
  withTiming, 
  interpolate,
  Easing 
} from 'react-native-reanimated';
import { useTranslation } from 'react-i18next';
import Svg, { Line, Circle, Defs, LinearGradient as SVGLinearGradient, Stop } from 'react-native-svg';
import { debounce } from 'lodash';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import * as apis from '@api/api';
import DropDownPicker from 'react-native-dropdown-picker';
import ErrorBoundary from '@utils/ErrorBoundary';
import { getContextSize, getContextBreakdown, updateContextSettings, getContextSettings, clearContext, getContextEntries, processSummaryResponse, setupSocketListener, ContextSettings as ContextSettingsType } from '@services/contextTracker';
import { Switch } from 'react-native';
const { width: screenWidth, height: screenHeight } = Dimensions.get('window');

// Constants for the interactive canvas - Reduced size for Android to prevent crashes
const CANVAS_SIZE = Platform.OS === 'android'
  ? { width: 1500, height: 1500 }
  : { width: 2000, height: 2000 };
const GRID_SIZE = 50;


interface ContextWeight {
  data: number;
  projects: number;
  media: number;
  integrations: number;
  knowledge: number;
}

interface ContextPosition {
  x: number;
  y: number;
}


interface ContextItem {
  key: string;
  label: string;
  icon: string;
  iconLibrary: string;
  color: string;
  gradientColors: string[];
  weight: number;
}

// Enhanced DraggableContextCircle component with UserHierarchy pattern
interface DraggableContextCircleProps {
  item: ContextItem;
  initialPosition: ContextPosition;
  onPositionChange: (key: string, position: ContextPosition) => void;
  theme: boolean;
  zoomLevel: number;
  isDragging: string | null;
  setIsDragging: (value: string | null) => void;
  zoomableViewRef?: any;
}

interface DraggableContextCircleHandle {
  getPosition: () => ContextPosition;
  setPosition: (position: ContextPosition) => void;
}

const DraggableContextCircle = React.forwardRef<DraggableContextCircleHandle, DraggableContextCircleProps>(
  ({ item, initialPosition, onPositionChange, theme, zoomLevel, isDragging, setIsDragging }, ref) => {
    // Use shared values initialized from initialPosition (exact UserHierarchy pattern)
    const translationX = useSharedValue(initialPosition.x);
    const translationY = useSharedValue(initialPosition.y);
    const hasBeenInitialized = useRef(false);
    const componentMountedRef = useRef(true);
    const isDragMovingRef = useRef(false);
    const startPositionRef = useRef({ x: 0, y: 0 });
    
    // Animated border gradient effect
    const borderAnimation = useSharedValue(0);
    
    // Start border animation on mount
    React.useEffect(() => {
      borderAnimation.value = withRepeat(
        withTiming(1, { duration: 2000, easing: Easing.inOut(Easing.sin) }),
        -1,
        true
      );
    }, []);

    // Imperative handle for direct position control (exact UserHierarchy pattern)
    React.useImperativeHandle(ref, () => ({
      setPosition: (position: ContextPosition) => {
        translationX.value = position.x;
        translationY.value = position.y;
        hasBeenInitialized.current = true;
        onPositionChange(item.key, position);
      },
      getPosition: () => ({
        x: translationX.value,
        y: translationY.value
      })
    }), [item.key, translationX, translationY, onPositionChange]);

    // Position initialization (exact UserHierarchy pattern)
    React.useEffect(() => {
      if (!hasBeenInitialized.current && (initialPosition.x !== 0 || initialPosition.y !== 0)) {
        translationX.value = initialPosition.x;
        translationY.value = initialPosition.y;
        hasBeenInitialized.current = true;
        onPositionChange(item.key, initialPosition);
      }
    }, [initialPosition.x, initialPosition.y, item.key, onPositionChange]);

    // Debounced connection line update during drag (exact UserHierarchy pattern)
    const debouncedConnectionUpdate = useCallback(
      debounce((newPos: ContextPosition) => {
        if (!componentMountedRef.current) return;
        // Update position in parent's ref for connection line updates
        onPositionChange(item.key, newPos);
      }, 50), // Connection updates every 50ms during drag for smooth lines
      [item.key, onPositionChange]
    );

    // Calculate circle properties
    const baseSize = 140;
    const maxSize = 220;
    const size = baseSize + (item.weight / 100) * (maxSize - baseSize);
    const isHollow = item.weight === 0;

    // PanResponder that matches UserHierarchy exactly - Android-safe version
    const panResponder = PanResponder.create({
      onStartShouldSetPanResponder: (evt, gestureState) => {
        // On Android, be more selective to avoid conflicts with zoomable view
        if (Platform.OS === 'android') {
          // Only capture if it's a clear tap/drag on the circle itself
          return true;
        }
        return true;
      },
      onMoveShouldSetPanResponder: (evt, gestureState) => {
        // On Android, require movement before capturing
        if (Platform.OS === 'android') {
          return Math.abs(gestureState.dx) > 5 || Math.abs(gestureState.dy) > 5;
        }
        return true;
      },
      onPanResponderGrant: (event, gestureState) => {
        // Get current position from shared values (exact UserHierarchy pattern)
        const currentPosition = { x: translationX.value, y: translationY.value };

        setIsDragging(item.key);
        isDragMovingRef.current = true;
        startPositionRef.current = currentPosition;
        
        // CRITICAL FIX: Ensure shared values don't reset during drag start (UserHierarchy pattern)
        translationX.value = currentPosition.x;
        translationY.value = currentPosition.y;
      },

      onPanResponderMove: (event, gestureState) => {
        // Handle drag movement with zoom compensation (exact UserHierarchy pattern)
        const { dx, dy } = gestureState;

        const compensatedDx = dx / (zoomLevel || 1);
        const compensatedDy = dy / (zoomLevel || 1);

        // CRITICAL FIX: Always use startPositionRef for consistency (UserHierarchy pattern)
        const newX = startPositionRef.current.x + compensatedDx;
        const newY = startPositionRef.current.y + compensatedDy;

        // Update shared values directly (UserHierarchy pattern)
        translationX.value = newX;
        translationY.value = newY;

        const newPosition = {
          x: newX,
          y: newY,
        };

        debouncedConnectionUpdate(newPosition);
      },

      onPanResponderRelease: (event, gestureState) => {
        setIsDragging(null);
        isDragMovingRef.current = false;

        // Final position update (UserHierarchy pattern)
        const finalPosition = {
          x: translationX.value,
          y: translationY.value
        };

        onPositionChange(item.key, finalPosition);
      },
      onPanResponderTerminationRequest: () => {
        // On Android, allow termination to prevent conflicts
        if (Platform.OS === 'android') {
          return true;
        }
        return false;
      }
    });

    // Cleanup on unmount (UserHierarchy pattern)
    React.useEffect(() => {
      return () => {
        componentMountedRef.current = false;
        debouncedConnectionUpdate.cancel();
      };
    }, [debouncedConnectionUpdate]);    const animatedStyle = useAnimatedStyle(() => {
      const borderOpacity = interpolate(borderAnimation.value, [0, 1], [0.3, 1]);
      
      return {
        transform: [
          { translateX: translationX.value },
          { translateY: translationY.value },
          { scale: 1 }
        ],
        shadowOpacity: isDragging === item.key ? 0.6 : 0.4,
        borderColor: `rgba(255, 255, 255, ${borderOpacity})`,
      } as any;
    });

    const renderIcon = (size = 32) => {
      const iconProps = {
        size,
        color: isHollow ? item.color : 'white',
        style: { 
          textShadowColor: 'rgba(0,0,0,0.3)', 
          textShadowRadius: 2 
        }
      };

      switch (item.iconLibrary) {
        case 'FontAwesome5':
          return <FontAwesome5 name={item.icon as any} {...iconProps} />;
        case 'Ionicons':
          return <Ionicons name={item.icon as any} {...iconProps} />;
        default:
          return <MaterialIcons name={item.icon as any} {...iconProps} />;
      }
    };

    return (
      <Animated.View
        {...panResponder.panHandlers}
        style={[
          {
            position: 'absolute',
            width: size,
            height: size,
            zIndex: isDragging === item.key ? 1000 : 1,
          },
          animatedStyle as any
        ]}
      >
          {/* Hollow circle for 0% weight */}
          {isHollow ? (
            <View
              style={{
                width: size,
                height: size,
                borderRadius: size / 2,
                borderWidth: 4,
                borderColor: item.color,
                backgroundColor: 'transparent',
                alignItems: 'center',
                justifyContent: 'center',
               
              }}
            >
            <View style={{ alignItems: 'center', gap: 6 }}>
                {renderIcon(28)}
                <Text
                  style={{
                    color: item.color,
                    fontSize: 14,
                    fontWeight: '600',
                  textAlign: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  {item.label}
                </Text>
                <Text
                  style={{
                    color: item.color,
                    fontSize: 12,
                    opacity: 0.8,
                  textAlign: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  {item.weight}%
                </Text>
              </View>
            </View>
          ) : (
            /* Filled circle with gradient for weights > 0% */
            <LinearGradient
              colors={[item.gradientColors[0], item.gradientColors[1]] as const}
              style={{
                width: size,
                height: size,
                borderRadius: size / 2,
                alignItems: 'center',
                justifyContent: 'center',
               
                borderWidth: 3,
                borderColor: 'rgba(255,255,255,0.2)',
              }}
            >
              <View style={{ alignItems: 'center', gap: 6 }}>
                {renderIcon(32)}
                <Text
                  style={{
                    color: 'white',
                    fontSize: 14,
                    fontWeight: '600',
                    textAlign: 'center',
                    textShadowColor: 'rgba(0,0,0,0.3)',
                    textShadowRadius: 2,
                    pointerEvents: 'none'
                  }}
                >
                  {item.label}
                </Text>
                <Text
                  style={{
                    color: 'white',
                    fontSize: 12,
                    fontWeight: '500',
                    textAlign: 'center',
                    textShadowColor: 'rgba(0,0,0,0.3)',
                    textShadowRadius: 1,
                    pointerEvents: 'none'
                  }}
                >
                  {item.weight}%
                </Text>
              </View>
            </LinearGradient>
          )}
        </Animated.View>
    );
  }
);

// Connection Lines Component to draw links between user card and context circles
interface ConnectionLinesProps {
  userPosition: ContextPosition;
  contextPositionsRef: React.MutableRefObject<Record<string, ContextPosition>>;
  contextItems: ContextItem[];
  theme: boolean;
  zoomLevel: number;
}

interface ConnectionLinesHandle {
  forceUpdate: () => void;
}

const ConnectionLines = React.forwardRef<ConnectionLinesHandle, ConnectionLinesProps>(({ 
  userPosition, 
  contextPositionsRef, 
  contextItems, 
  theme, 
  zoomLevel 
}, ref) => {
  const [renderKey, setRenderKey] = useState(0);

  // Expose force update method (UserHierarchy pattern)
  React.useImperativeHandle(ref, () => ({
    forceUpdate: () => {
      setRenderKey(prev => prev + 1);
    }
  }));

  // Read positions from ref directly (UserHierarchy pattern)
  const contextPositions = contextPositionsRef.current;

  if (!userPosition || Object.keys(contextPositions).length === 0) return null;

  const lineOpacity = Math.max(0.3, Math.min(0.8, zoomLevel * 0.5 + 0.3));

  // Android-specific: Use try-catch for SVG rendering
  try {
    return (
      <Svg
        style={StyleSheet.absoluteFillObject}
        width="100%"
        height="100%"
        {...(Platform.OS === 'android' && {
          renderToHardwareTextureAndroid: true,
          shouldRasterizeIOS: false
        })}
      >
        <Defs>
          {contextItems.map((item) => (
            <SVGLinearGradient key={item.key} id={`gradient-${item.key}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <Stop offset="0%" stopColor={item.gradientColors[0]} stopOpacity={lineOpacity} />
              <Stop offset="50%" stopColor={item.gradientColors[1]} stopOpacity={lineOpacity} />
              <Stop offset="100%" stopColor={item.gradientColors[0]} stopOpacity={lineOpacity * 0.6} />
            </SVGLinearGradient>
          ))}
        </Defs>

        {contextItems.map((item) => {
          const contextPos = contextPositions[item.key];
          if (!contextPos) return null;

          // Calculate line from user card center to context circle center
          const userCenterX = userPosition.x + 100; // User card width/2
          const userCenterY = userPosition.y + 80;  // User card height/2

          // Calculate circle size to get center point
          const baseSize = 140;
          const maxSize = 220;
          const circleSize = baseSize + (item.weight / 100) * (maxSize - baseSize);
          const contextCenterX = contextPos.x + circleSize / 2;
          const contextCenterY = contextPos.y + circleSize / 2;

          // Line thickness based on weight
          const lineWidth = Math.max(2, (item.weight / 100) * 6);

          return (
            <Line
              key={`connection-${item.key}`}
              x1={userCenterX}
              y1={userCenterY}
              x2={contextCenterX}
              y2={contextCenterY}
              stroke={`url(#gradient-${item.key})`}
              strokeWidth={lineWidth}
              opacity={lineOpacity}
              strokeDasharray={item.weight === 0 ? "8,4" : undefined} // Dashed line for 0% weight - undefined instead of "none" for Android
            />
          );
        })}
      </Svg>
    );
  } catch (error) {
    console.error('❌ SVG rendering error on Android:', error);
    // Return empty view on error to prevent crash
    return null;
  }
});

// Floating Slider Controls Component
interface FloatingSliderControlsProps {
  contextItems: any[];
  weights: ContextWeight;
  onWeightChange: (key: keyof ContextWeight, value: number) => void;
  theme: boolean;
}

const FloatingSliderControls: React.FC<FloatingSliderControlsProps> = ({
  contextItems,
  weights,
  onWeightChange,
  theme
}) => {
  const [isExpanded, setIsExpanded] = useState(Platform.OS === 'web'); // Collapsed by default on mobile

  const renderIcon = (item: any, size = 12) => {
    const iconProps = { size, color: 'white' };
    switch (item.iconLibrary) {
      case 'FontAwesome5':
        return <FontAwesome5 name={item.icon} {...iconProps} />;
      case 'Ionicons':
        return <Ionicons name={item.icon} {...iconProps} />;
      default:
        return <MaterialIcons name={item.icon} {...iconProps} />;
    }
  };

  return (
    <View
      style={[
        {
          position: 'absolute',
          top: Platform.OS === 'web' ? 20 : 20,
          bottom: Platform.OS === 'web' ? undefined : undefined,
          right: Platform.OS === 'web' ? 20 : 20,
          left: Platform.OS === 'web' ? undefined : 20,
          maxHeight: isExpanded ? '80%' : 60,
          width: Platform.OS === 'web' ? 280 : undefined,
          maxWidth: Platform.OS === 'web' ? 280 : '90%',
          paddingVertical: 0,
          paddingBottom: isExpanded && 40,
          zIndex: 2000,
          backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(42,42,42,0.95)',
          borderWidth: 1,
          borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)',
        },
        theme ? styles.profileCard : styles.profileCardDark,
        Platform.OS === 'web' ? { backdropFilter: 'blur(10px)' } as any : {}
      ]}
    >
      <YStack space="$3" pointerEvents="auto">
        <Pressable
          onPress={() => Platform.OS !== 'web' && setIsExpanded(!isExpanded)}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingVertical: Platform.OS === 'web' ? 0 : 4
          }}
        >
          <H3 style={{
            color: getModernTextColor(theme),
            textAlign: 'center',
            fontSize: 16,
            marginBottom: Platform.OS === 'web' ? 8 : 0,
            flex: 1
          }}>
            Context Controls
          </H3>
          {Platform.OS !== 'web' && (
            <MaterialIcons
              name={isExpanded ? 'expand-less' : 'expand-more'}
              size={24}
              color={getModernTextColor(theme)}
            />
          )}
        </Pressable>

        {isExpanded && contextItems.map((item) => (
          <YStack key={item.key} space="$2">
            <XStack justifyContent="space-between" alignItems="center">
              <XStack alignItems="center" space="$2">
                <View
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 10,
                    backgroundColor: item.color,
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {renderIcon(item)}
                </View>
                <SizableText
                  size="$3"
                  fontWeight="500"
                  style={{ 
                    color: getModernTextColor(theme)
                  }}
                >
                  {item.label}
                </SizableText>
              </XStack>
              <SizableText
                size="$3"
                fontWeight="600"
                style={{ 
                  color: item.color, 
                  minWidth: 40, 
                  textAlign: 'right'
                }}
              >
                {item.weight}%
              </SizableText>
            </XStack>

            <View
              style={Platform.OS === 'web' ? {
                touchAction: 'auto',
                pointerEvents: 'auto',
                cursor: 'pointer',
              } as any : {}}
            >
            <Slider
              size="$3"
              width="100%"
                value={[item.weight]}
              max={100}
              min={0}
              step={5}
              onValueChange={(value) => onWeightChange(item.key as keyof ContextWeight, value[0])}
              style={{ opacity: 0.8 }}
                {...(Platform.OS === 'web' && {
                  cursor: 'pointer' as any,
                  pointerEvents: 'auto' as any
                })}
            >
                <Slider.Track
                  backgroundColor={theme ? '#e2e8f0' : '#475569'}
                  {...(Platform.OS === 'web' && {
                    cursor: 'pointer' as any,
                    pointerEvents: 'auto' as any
                  })}
                >
                <Slider.TrackActive backgroundColor={item.color} />
              </Slider.Track>
              <Slider.Thumb
                size="$1"
                index={0}
                backgroundColor={item.color}
                borderColor="white"
                borderWidth={2}
                shadowColor={item.color}
                shadowOffset={{ width: 0, height: 2 }}
                shadowOpacity={0.3}
                shadowRadius={4}
                elevation={4}
                  {...(Platform.OS === 'web' && {
                    cursor: 'grab' as any,
                    pointerEvents: 'auto' as any
                  })}
              />
            </Slider>
            </View>
          </YStack>
        ))}
      </YStack>
    </View>
  );
};

// Separate Context Health Card component to prevent header re-renders
interface ContextHealthCardProps {
  theme: boolean;
  title?: string;
  subtitle?: string;
  contextHealthData: {
    score: number;
    status: string;
    recommendations: string[];
    completionPercentage?: number;
    profileCompletion?: any;
  };
}

const ContextHealthCard: React.FC<ContextHealthCardProps> = React.memo(({ title, subtitle, theme, contextHealthData }) => {
  const [showHealthDropdown, setShowHealthDropdown] = useState(false);

  const getHealthColor = (score: number) => {
    if (score >= 90) return { color: theme ? "#059669" : "#10b981", bgColor: theme ? "#d1fae5" : "#064e3b" };
    if (score >= 70) return { color: theme ? "#f59e0b" : "#fbbf24", bgColor: theme ? "#fef3c7" : "#451a03" };
    return { color: theme ? "#ef4444" : "#f87171", bgColor: theme ? "#fee2e2" : "#7f1d1d" };
  };

  const healthColors = getHealthColor(contextHealthData.score);

  return (
    <Animatable.View
      animation="bounceIn"
      delay={400}
      style={{
        flexBasis: Platform.OS === 'web' ? '48%' : '100%',
        flexGrow: 1,
        minWidth: 300,
        backgroundColor: healthColors.bgColor,
        borderRadius: 16,
        padding: 20,
        marginBottom: 8,
        borderWidth: 1,
        borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <MaterialIcons
            name="health-and-safety"
            size={24}
            color={healthColors.color}
            style={{ marginRight: 12 }}
          />
          <Text style={{
            fontSize: 18,
            fontWeight: '700',
            color: healthColors.color,
            pointerEvents: 'none'
          }}>
            Context Health
          </Text>
        </View>
        <Pressable
          onPress={() => setShowHealthDropdown(!showHealthDropdown)}
          style={{
            padding: 4,
            borderRadius: 8,
            backgroundColor: 'rgba(0,0,0,0.1)'
          }}
        >
          <MaterialIcons
            name={showHealthDropdown ? "keyboard-arrow-up" : "keyboard-arrow-down"}
            size={20}
            color={healthColors.color}
          />
        </Pressable>
      </View>

      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
        <View style={{
          width: 60,
          height: 60,
          borderRadius: 30,
          backgroundColor: healthColors.color + '20',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: 16
        }}>
          <Text style={{
            fontSize: 20,
            fontWeight: '800',
            color: healthColors.color,
            pointerEvents: 'none'
          }}>
            {contextHealthData.score}
          </Text>
        </View>
        <View>
          <Text style={{
            fontSize: 16,
            fontWeight: '600',
            color: healthColors.color,
            marginBottom: 4,
            pointerEvents: 'none'
          }}>
            {contextHealthData.status}
          </Text>
          <Text style={{
            fontSize: 12,
            color: theme ? "#64748b" : "#94a3b8",
            pointerEvents: 'none'
          }}>
            {contextHealthData.completionPercentage !== undefined
              ? `Profile: ${contextHealthData.completionPercentage}% complete`
              : 'Overall system performance'
            }
          </Text>
        </View>
      </View>

      {/* Health Score Progress */}
      <View style={{
        height: 8,
        backgroundColor: theme ? "#f1f5f9" : "#334155",
        borderRadius: 4,
        overflow: 'hidden',
        marginBottom: 8
      }}>
        <View style={{
          height: '100%',
          width: `${contextHealthData.score}%`,
          backgroundColor: healthColors.color,
          borderRadius: 4
        }} />
      </View>

      {/* Profile Completion Progress */}
      {contextHealthData.completionPercentage !== undefined && (
        <View style={{
          height: 6,
          backgroundColor: theme ? "#e2e8f0" : "#475569",
          borderRadius: 3,
          overflow: 'hidden',
          marginBottom: showHealthDropdown ? 16 : 0
        }}>
          <View style={{
            height: '100%',
            width: `${contextHealthData.completionPercentage}%`,
            backgroundColor: theme ? "#3B82F6" : "#60A5FA",
            borderRadius: 3
          }} />
        </View>
      )}

      {/* Recommendations Dropdown */}
      {showHealthDropdown && (
        <Animatable.View animation="slideInDown" duration={300}>
          <Text style={{
            fontSize: 14,
            fontWeight: '600',
            color: healthColors.color,
            marginBottom: 12,
            pointerEvents: 'none'
          }}>
            AI Recommendations:
          </Text>
          {contextHealthData.recommendations.map((recommendation, index) => (
            <View key={index} style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              marginBottom: 8,
              paddingLeft: 8
            }}>
              <MaterialIcons
                name="check-circle"
                size={16}
                color={healthColors.color}
                style={{ marginRight: 8, marginTop: 2 }}
              />
              <Text style={{
                fontSize: 12,
                color: theme ? "#64748b" : "#94a3b8",
                flex: 1,
                lineHeight: 16,
                pointerEvents: 'none'
              }}>
                {recommendation}
              </Text>
            </View>
          ))}
        </Animatable.View>
      )}
    </Animatable.View>
  );
});

// Header Component is now imported from separate file to prevent re-renders

// Context Settings Card Component
interface ContextSettingsCardProps {
  theme: boolean;
  onContextUpdate: (context: any) => void;
  currentContext?: any;
}

const ContextSettingsCard: React.FC<ContextSettingsCardProps> = React.memo(({ 
  theme, 
  onContextUpdate, 
  currentContext 
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);



  const [contextData, setContextData] = useState({
    industry: currentContext?.industry || '',
    company_size: currentContext?.company_size || '',
    experience_level: currentContext?.experience_level || '',
    preferred_language: currentContext?.preferred_language || 'en',
    location: currentContext?.location || '',
    business_type: currentContext?.business_type || '',
    key_interests: currentContext?.key_interests || [],
    technical_skills: currentContext?.technical_skills || []
  });

  // Update context data when currentContext changes
  React.useEffect(() => {
    const newContextData = {
      industry: currentContext?.industry || '',
      company_size: currentContext?.company_size || '',
      experience_level: currentContext?.experience_level || '',
      preferred_language: currentContext?.preferred_language || 'en',
      location: currentContext?.location || '',
      business_type: currentContext?.business_type || '',
      key_interests: currentContext?.key_interests || [],
      technical_skills: currentContext?.technical_skills || []
    };

    // Only update if the data has actually changed to prevent unnecessary re-renders
    setContextData(prevData => {
      const hasChanged = JSON.stringify(prevData) !== JSON.stringify(newContextData);
      if (hasChanged) {
        console.log('🔄 Context data changed, updating state');
        console.log('🔄 Previous:', prevData);
        console.log('🔄 New:', newContextData);
        return newContextData;
      }
      return prevData;
    });

    // Close all dropdowns when context data changes
    setIndustryOpen(false);
    setCompanySizeOpen(false);
    setExperienceLevelOpen(false);
    setBusinessTypeOpen(false);
  }, [currentContext?.industry, currentContext?.company_size, currentContext?.experience_level, currentContext?.location, currentContext?.business_type, currentContext?.key_interests, currentContext?.technical_skills, currentContext?.updatedAt]);

  // Dropdown states
  const [industryOpen, setIndustryOpen] = useState(false);
  const [companySizeOpen, setCompanySizeOpen] = useState(false);
  const [experienceLevelOpen, setExperienceLevelOpen] = useState(false);
  const [businessTypeOpen, setBusinessTypeOpen] = useState(false);

  const queryClient = useQueryClient();

  // Update context mutation
  const updateContextMutation = useMutation({
    mutationFn: (contextData: any) => apis.updateUserContext(contextData),
    onSuccess: async (data) => {
      console.log('✅ Context update successful:', data);
      onContextUpdate(data);
      Alert.alert('Success', 'Context settings updated successfully!');
    },
    onError: (error) => {
      console.error('❌ Error updating context:', error);
      Alert.alert('Error', 'Failed to update context settings. Please try again.');
    },
  });

  const industries = [
    { label: 'Technology', value: 'Technology' },
    { label: 'Healthcare', value: 'Healthcare' },
    { label: 'Finance', value: 'Finance' },
    { label: 'Education', value: 'Education' },
    { label: 'Retail', value: 'Retail' },
    { label: 'Manufacturing', value: 'Manufacturing' },
    { label: 'Real Estate', value: 'Real Estate' },
    { label: 'Marketing', value: 'Marketing' },
    { label: 'Consulting', value: 'Consulting' },
    { label: 'Media', value: 'Media' },
    { label: 'Non-profit', value: 'Non-profit' },
    { label: 'Government', value: 'Government' },
    { label: 'Agriculture', value: 'Agriculture' },
    { label: 'Energy', value: 'Energy' },
    { label: 'Transportation', value: 'Transportation' },
    { label: 'Other', value: 'Other' }
  ];

  const companySizes = [
    { label: 'Freelancer/Solo', value: 'Freelancer/Solo' },
    { label: '2-10 employees', value: '2-10 employees' },
    { label: '11-50 employees', value: '11-50 employees' },
    { label: '51-200 employees', value: '51-200 employees' },
    { label: '201-1000 employees', value: '201-1000 employees' },
    { label: '1000+ employees', value: '1000+ employees' }
  ];

  const experienceLevels = [
    { label: 'Beginner (0-2 years)', value: 'Beginner (0-2 years)' },
    { label: 'Intermediate (3-5 years)', value: 'Intermediate (3-5 years)' },
    { label: 'Advanced (6-10 years)', value: 'Advanced (6-10 years)' },
    { label: 'Expert (10+ years)', value: 'Expert (10+ years)' },
    { label: 'Executive/Leadership', value: 'Executive/Leadership' }
  ];

  const businessTypes = [
    { label: 'B2B', value: 'B2B' },
    { label: 'B2C', value: 'B2C' },
    { label: 'B2B2C', value: 'B2B2C' },
    { label: 'SaaS', value: 'SaaS' },
    { label: 'E-commerce', value: 'E-commerce' },
    { label: 'Service-based', value: 'Service-based' },
    { label: 'Product-based', value: 'Product-based' },
    { label: 'Marketplace', value: 'Marketplace' },
    { label: 'Agency', value: 'Agency' },
    { label: 'Startup', value: 'Startup' },
    { label: 'Enterprise', value: 'Enterprise' }
  ];

  const handleSave = () => {
    console.log('💾 Saving context data:', contextData);
    updateContextMutation.mutate(contextData);
  };

  const handleInputChange = (field: string, value: any) => {
    console.log(`🔄 Updating ${field} to:`, value);
    setContextData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      console.log('🔄 Updated contextData:', updated);
      return updated;
    });
  };

  return (
    <View
      style={{
        backgroundColor: theme ? "#ffffff" : "#1e293b",
        borderRadius: 20,
        padding: 24,
        marginVertical: 16,
        marginHorizontal: 20,
        borderWidth: 1,
        borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
        minHeight: isExpanded ? 600 : 120,
        overflow: 'hidden'
      }}
    >
        {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}>
        <View style={{
          backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
          borderRadius: 12,
          padding: 12,
          marginRight: 16
        }}>
          <MaterialIcons
            name="tune"
            size={28}
            color={theme ? "#0ea5e9" : "#38bdf8"}
          />
        </View>
        <View>
          <SizableText style={{
            color: theme ? "#1e293b" : "#f1f5f9",
            fontWeight: '700',
            marginBottom: 4
          }}>
            {t('Context Settings')}
          </SizableText>
          <Text style={{
            color: theme ? "#64748b" : "#94a3b8",
            fontSize: 14
          }}>
            {t('Configure your industry and preferences')}
          </Text>
        </View>
      </View>

          <Animatable.View animation="fadeIn" duration={300}>
        <ScrollView style={{ maxHeight: 500 }} showsVerticalScrollIndicator={false}>
              <YStack space="$4">
                {/* Industry Selection */}
                <YStack space="$2">
                  <SizableText style={{ 
                    color: getModernTextColor(theme, 'primary'),
                    fontSize: 16,
                    fontWeight: '600'
                  }}>
                    {t('Industry')}
                  </SizableText>
                  <DropDownPicker
                key={`industry-${contextData.industry}`}
                    items={industries}
                    open={industryOpen}
                    value={contextData.industry}
                    setOpen={setIndustryOpen}
                    setValue={(callback) => {
                      const value = typeof callback === 'function' ? callback(contextData.industry) : callback;
                      handleInputChange('industry', value);
                    }}
                    placeholder="Select your industry"
                    zIndex={4000}
                    zIndexInverse={1000}
                    style={{
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      borderRadius: 12,
                      paddingHorizontal: 16,
                      minHeight: 50,
                    }}
                    textStyle={{
                      color: theme ? '#1e293b' : '#f8fafc',
                      fontSize: 16,
                    }}
                    dropDownContainerStyle={{
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      borderRadius: 12,
                      maxHeight: 200,
                    }}
                    listMode="SCROLLVIEW"
                    scrollViewProps={{
                      nestedScrollEnabled: true
                    }}
                  />
                </YStack>

            {/* Company Size Selection */}
                <YStack space="$2">
                  <SizableText style={{ 
                    color: getModernTextColor(theme, 'primary'),
                    fontSize: 16,
                    fontWeight: '600'
                  }}>
                    {t('Company Size')}
                  </SizableText>
                  <DropDownPicker
                key={`company_size-${contextData.company_size}`}
                    items={companySizes}
                    open={companySizeOpen}
                    value={contextData.company_size}
                    setOpen={setCompanySizeOpen}
                    setValue={(callback) => {
                      const value = typeof callback === 'function' ? callback(contextData.company_size) : callback;
                      handleInputChange('company_size', value);
                    }}
                placeholder="Select your company size"
                    zIndex={3000}
                    zIndexInverse={2000}
                    style={{
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      borderRadius: 12,
                      paddingHorizontal: 16,
                      minHeight: 50,
                    }}
                    textStyle={{
                      color: theme ? '#1e293b' : '#f8fafc',
                      fontSize: 16,
                    }}
                    dropDownContainerStyle={{
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      borderRadius: 12,
                      maxHeight: 200,
                    }}
                    listMode="SCROLLVIEW"
                    scrollViewProps={{
                      nestedScrollEnabled: true
                    }}
                  />
                </YStack>

            {/* Experience Level Selection */}
                <YStack space="$2">
                  <SizableText style={{ 
                    color: getModernTextColor(theme, 'primary'),
                    fontSize: 16,
                    fontWeight: '600'
                  }}>
                    {t('Experience Level')}
                  </SizableText>
                  <DropDownPicker
                key={`experience_level-${contextData.experience_level}`}
                    items={experienceLevels}
                    open={experienceLevelOpen}
                    value={contextData.experience_level}
                    setOpen={setExperienceLevelOpen}
                    setValue={(callback) => {
                      const value = typeof callback === 'function' ? callback(contextData.experience_level) : callback;
                      handleInputChange('experience_level', value);
                    }}
                placeholder="Select your experience level"
                    zIndex={2000}
                    zIndexInverse={3000}
                    style={{
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      borderRadius: 12,
                      paddingHorizontal: 16,
                      minHeight: 50,
                    }}
                    textStyle={{
                      color: theme ? '#1e293b' : '#f8fafc',
                      fontSize: 16,
                    }}
                    dropDownContainerStyle={{
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      borderRadius: 12,
                      maxHeight: 200,
                    }}
                    listMode="SCROLLVIEW"
                    scrollViewProps={{
                      nestedScrollEnabled: true
                    }}
                  />
                </YStack>

            {/* Business Type Selection */}
                <YStack space="$2">
                  <SizableText style={{ 
                    color: getModernTextColor(theme, 'primary'),
                    fontSize: 16,
                    fontWeight: '600'
                  }}>
                    {t('Business Type')}
                  </SizableText>
                  <DropDownPicker
                key={`business_type-${contextData.business_type}`}
                    items={businessTypes}
                    open={businessTypeOpen}
                    value={contextData.business_type}
                    setOpen={setBusinessTypeOpen}
                    setValue={(callback) => {
                      const value = typeof callback === 'function' ? callback(contextData.business_type) : callback;
                      handleInputChange('business_type', value);
                    }}
                placeholder="Select your business type"
                    zIndex={1000}
                    zIndexInverse={4000}
                    style={{
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      borderRadius: 12,
                      paddingHorizontal: 16,
                      minHeight: 50,
                    }}
                    textStyle={{
                      color: theme ? '#1e293b' : '#f8fafc',
                      fontSize: 16,
                    }}
                    dropDownContainerStyle={{
                      backgroundColor: theme ? 'white' : '#1e293b',
                      borderColor: theme ? '#e2e8f0' : '#374151',
                      borderRadius: 12,
                      maxHeight: 200,
                    }}
                    listMode="SCROLLVIEW"
                    scrollViewProps={{
                      nestedScrollEnabled: true
                    }}
                  />
                </YStack>

            {/* Save Button */}
            <XStack justifyContent="flex-end" marginTop="$4">
                  <Button
                size="$4"
                backgroundColor={theme ? '#3B82F6' : '#1E40AF'}
                color="white"
                borderRadius={12}
                paddingHorizontal={24}
                    onPress={handleSave}
                    disabled={updateContextMutation.isPending}
                opacity={updateContextMutation.isPending ? 0.7 : 1}
                  >
                    <SizableText style={{ color: '#fff' }}>
                      {updateContextMutation.isPending ? t('Saving...') : t('Save')}
                    </SizableText>
                  </Button>
                </XStack>
              </YStack>
            </ScrollView>
      </Animatable.View>
    </View>
  );
});

// Context Window Settings Card Component
interface ContextWindowSettingsCardProps {
  theme: boolean;
}

const ContextWindowSettingsCard: React.FC<ContextWindowSettingsCardProps> = React.memo(({ theme }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<ContextSettingsType>({
    trackUserInteractions: true,
    trackNotifications: true,
    trackAIRequests: true,
    trackTensorFlowRequests: true,
    trackDocumentViewing: true,
    userInteractionFilter: 'filtered'
  });
  const [contextSize, setContextSize] = useState({ tokens: 0, bytes: 0 });
  const [breakdown, setBreakdown] = useState<any>(null);

  // Load settings and context stats
  useEffect(() => {
    const loadData = async () => {
      if (Platform.OS !== 'web') return;

      try {
        const [currentSettings, size, breakdownData] = await Promise.all([
          getContextSettings(),
          getContextSize(),
          getContextBreakdown()
        ]);
        setSettings(currentSettings);
        setContextSize(size);
        setBreakdown(breakdownData);
      } catch (error) {
        console.error('Failed to load context window data:', error);
      }
    };

    loadData();
    const interval = setInterval(loadData, 5000); // Update every 5 seconds

    // Listen for context summary generation events to refresh immediately
    const handleSummaryGenerated = async () => {
      try {
        const [size, breakdownData] = await Promise.all([
          getContextSize(),
          getContextBreakdown()
        ]);
        setContextSize(size);
        setBreakdown(breakdownData);
      } catch (error) {
        console.error('Failed to refresh context after summary:', error);
      }
    };

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.addEventListener('contextSummaryGenerated', handleSummaryGenerated);
    }

    return () => {
      clearInterval(interval);
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.removeEventListener('contextSummaryGenerated', handleSummaryGenerated);
      }
    };
  }, []);

  const handleSettingChange = async (key: keyof ContextSettingsType, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    try {
      await updateContextSettings(newSettings);
    } catch (error) {
      console.error('Failed to update context settings:', error);
      Alert.alert('Error', 'Failed to update settings');
    }
  };

  const handleClearContext = async () => {
    Alert.alert(
      'Clear Context',
      'Are you sure you want to clear all context data? This cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Clear',
          style: 'destructive',
          onPress: async () => {
            try {
              await clearContext();
              setContextSize({ tokens: 0, bytes: 0 });
              setBreakdown(null);
              Alert.alert('Success', 'Context cleared successfully');
            } catch (error) {
              Alert.alert('Error', 'Failed to clear context');
            }
          }
        }
      ]
    );
  };

  const handleTriggerSummary = async () => {
    try {
      const entries = await getContextEntries();
      if (entries.length === 0) {
        Alert.alert('Info', 'No context entries to summarize');
        return;
      }

      const originalEntryCount = entries.length;

      // Ensure socket listener is set up to receive summary completion notifications
      if (Platform.OS === 'web') {
        await setupSocketListener();
      }

      // Show alert that summary is being generated
      if (Platform.OS === 'web') {
        alert('Generating Summary', 'This may take a moment...');
      } else {
        Alert.alert('Generating Summary', 'This may take a moment...');
      }

      // Call the API - it returns { success: true, status: 'queued' }
      // The actual summary will be processed via socket notification
      const response = await apis.summarizeContext(entries);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to queue summary generation');
      }

      // The summary processing happens asynchronously via socket notification
      // The socket handler will call processSummaryResponse which clears context and saves summary
      // We just need to inform the user that it's being processed
      if (Platform.OS === 'web') {
        alert('Summary Generation Queued', 'The summary is being generated in the background. You will be notified when it completes.');
      } else {
        Alert.alert('Summary Generation Queued', 'The summary is being generated in the background. You will be notified when it completes.');
      }

      // Refresh stats after a short delay to allow socket notification to process
      // The event listener will also refresh when the summary is ready
      setTimeout(async () => {
        try {
          const [size, breakdownData] = await Promise.all([
            getContextSize(),
            getContextBreakdown()
          ]);
          setContextSize(size);
          setBreakdown(breakdownData);
        } catch (error) {
          console.error('Failed to refresh context after summary:', error);
        }
      }, 2000);
    } catch (error: any) {
      console.error('Failed to generate summary:', error);
      const errorMessage = error?.requiresPremium 
        ? 'Premium subscription required for context summarization'
        : 'Failed to generate summary';
      Alert.alert('Error', errorMessage);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  const percentage = Math.max(
    (contextSize.tokens / 200000) * 100,
    (contextSize.bytes / 200000) * 100
  );

  return (
    <View
      style={{
        backgroundColor: theme ? "#ffffff" : "#1e293b",
        borderRadius: 20,
        padding: 24,
        marginVertical: 16,
        marginHorizontal: 20,
        borderWidth: 1,
        borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)",
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
          <View style={{
            backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
            borderRadius: 12,
            padding: 12,
            marginRight: 16
          }}>
            <MaterialIcons
              name="memory"
              size={28}
              color={theme ? "#0ea5e9" : "#38bdf8"}
            />
          </View>
          <View style={{ flex: 1 }}>
            <SizableText style={{
              color: theme ? "#1e293b" : "#f1f5f9",
              fontWeight: '700',
              marginBottom: 4
            }}>
              Context Window Settings
            </SizableText>
            <Text style={{
              color: theme ? "#64748b" : "#94a3b8",
              fontSize: 14
            }}>
              {formatNumber(contextSize.tokens)}k tokens / {formatNumber(200000)}k ({Math.round(percentage)}%)
            </Text>
          </View>
        </View>
      </View>

      <YStack space="$4">
        {/* Usage Breakdown */}
        {breakdown && (
          <YStack space="$2">
            <SizableText style={{
              color: getModernTextColor(theme, 'primary'),
              fontSize: 16,
              fontWeight: '600'
            }}>
              Usage Breakdown
            </SizableText>
            <View style={{
              backgroundColor: theme ? "#f8fafc" : "#334155",
              borderRadius: 12,
              padding: 16,
              gap: 8
            }}>
              {Object.entries(breakdown).map(([type, data]: [string, any]) => {
                if (data.tokens === 0) return null;
                return (
                  <View key={type} style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingVertical: 8
                  }}>
                    <Text style={{
                      color: getModernTextColor(theme, 'primary'),
                      fontSize: 14,
                      flex: 1
                    }}>
                      {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Text>
                    <Text style={{
                      color: getModernTextColor(theme, 'secondary'),
                      fontSize: 12
                    }}>
                      {formatNumber(data.tokens)}k tokens • {data.count} entries
                    </Text>
                  </View>
                );
              })}
            </View>
          </YStack>
        )}

        {/* Tracking Toggles */}
        <YStack space="$3">
          <SizableText style={{
            color: getModernTextColor(theme, 'primary'),
            fontSize: 16,
            fontWeight: '600'
          }}>
            Track Categories
          </SizableText>

          {/* User Interactions */}
          <XStack justifyContent="space-between" alignItems="center">
            <YStack flex={1}>
              <Text style={{
                color: getModernTextColor(theme, 'primary'),
                fontSize: 14,
                fontWeight: '500'
              }}>
                User Interactions
              </Text>
              <Text style={{
                color: getModernTextColor(theme, 'secondary'),
                fontSize: 12,
                marginTop: 2
              }}>
                Track user actions and interactions
              </Text>
            </YStack>
            <Switch
              value={settings.trackUserInteractions && settings.userInteractionFilter !== 'none'}
              onValueChange={(value) => handleSettingChange('userInteractionFilter', value ? 'filtered' : 'none')}
            />
          </XStack>

          {/* Notifications */}
          <XStack justifyContent="space-between" alignItems="center">
            <YStack flex={1}>
              <Text style={{
                color: getModernTextColor(theme, 'primary'),
                fontSize: 14,
                fontWeight: '500'
              }}>
                Server Notifications
              </Text>
              <Text style={{
                color: getModernTextColor(theme, 'secondary'),
                fontSize: 12,
                marginTop: 2
              }}>
                Track server notifications and events
              </Text>
            </YStack>
            <Switch
              value={settings.trackNotifications}
              onValueChange={(value) => handleSettingChange('trackNotifications', value)}
            />
          </XStack>

          {/* AI Requests */}
          <XStack justifyContent="space-between" alignItems="center">
            <YStack flex={1}>
              <Text style={{
                color: getModernTextColor(theme, 'primary'),
                fontSize: 14,
                fontWeight: '500'
              }}>
                AI Requests
              </Text>
              <Text style={{
                color: getModernTextColor(theme, 'secondary'),
                fontSize: 12,
                marginTop: 2
              }}>
                Track AI API requests and responses
              </Text>
            </YStack>
            <Switch
              value={settings.trackAIRequests}
              onValueChange={(value) => handleSettingChange('trackAIRequests', value)}
            />
          </XStack>

          {/* TensorFlow Requests */}
          <XStack justifyContent="space-between" alignItems="center">
            <YStack flex={1}>
              <Text style={{
                color: getModernTextColor(theme, 'primary'),
                fontSize: 14,
                fontWeight: '500'
              }}>
                TensorFlow Requests
              </Text>
              <Text style={{
                color: getModernTextColor(theme, 'secondary'),
                fontSize: 12,
                marginTop: 2
              }}>
                Track proactive AI requests from TensorFlow
              </Text>
            </YStack>
            <Switch
              value={settings.trackTensorFlowRequests}
              onValueChange={(value) => handleSettingChange('trackTensorFlowRequests', value)}
            />
          </XStack>

          {/* Document Viewing */}
          <XStack justifyContent="space-between" alignItems="center">
            <YStack flex={1}>
              <Text style={{
                color: getModernTextColor(theme, 'primary'),
                fontSize: 14,
                fontWeight: '500'
              }}>
                Document Viewing
              </Text>
              <Text style={{
                color: getModernTextColor(theme, 'secondary'),
                fontSize: 12,
                marginTop: 2
              }}>
                Track time spent viewing documents and pages
              </Text>
            </YStack>
            <Switch
              value={settings.trackDocumentViewing}
              onValueChange={(value) => handleSettingChange('trackDocumentViewing', value)}
            />
          </XStack>
        </YStack>

        {/* Actions */}
        <XStack gap="$3" marginTop="$4">
          <Button
            size="$3"
            backgroundColor={theme ? '#EF4444' : '#DC2626'}
            color="white"
            borderRadius={12}
            flex={1}
            onPress={handleClearContext}
          >
            <SizableText style={{ color: '#fff', fontSize: 12 }}>
              Clear Context
            </SizableText>
          </Button>
          <Button
            size="$3"
            backgroundColor={theme ? '#3B82F6' : '#1E40AF'}
            color="white"
            borderRadius={12}
            flex={1}
            onPress={handleTriggerSummary}
          >
            <SizableText style={{ color: '#fff', fontSize: 12 }}>
              Generate Summary
            </SizableText>
          </Button>
        </XStack>
      </YStack>
    </View>
  );
});

const ContextScreen = () => {
  // Using specific selectors to prevent unnecessary re-renders
  const userContext = useSelector(selectUserContext);
  const userContextWeights = useSelector(selectUserContextWeights);
  const userId = useSelector(selectUserId);
  const userProfile = useSelector(selectUserProfile);
  const userAvatar = useSelector(selectUserAvatar);
  const userEmail = useSelector(selectUserEmail);
  const userName = useSelector(selectUserName);
  const theme = themeSelector();
  const { t } = useTranslation();

  // Debug user data loading (DEV only, no forced re-renders)
  React.useEffect(() => {
    if (__DEV__ && userContext) {
      console.log('🔍 Context data loaded:', {
        hasWeights: !!userContextWeights,
        industry: userContext.industry,
        companySize: userContext.company_size
      });
    }
  }, [userContext?.updatedAt]); // Only log when actually updated

  // Load latest user data on component mount to ensure fresh context data
  React.useEffect(() => {
    const loadLatestUserData = async () => {
      if (userId) {
        try {
          if (__DEV__) {
            console.log('🔄 Loading latest user data on mount...');
          }
          await apis.refreshUser();
          if (__DEV__) {
            console.log('✅ Latest user data loaded successfully');
          }
        } catch (error) {
          if (__DEV__) {
            console.error('❌ Failed to load latest user data:', error);
          }
        }
      }
    };

    loadLatestUserData();
  }, [userId]); // Only run when userId changes

  // State management (minimal state like UserHierarchy)
  const [weights, setWeights] = useState<ContextWeight>({
    data: 75,
    projects: 60,
    media: 45,
    integrations: 80,
    knowledge: 70
  });

  const [isDragging, setIsDragging] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isComponentMounted, setIsComponentMounted] = useState(true);
  const [scrollEnabled, setScrollEnabled] = useState(true);
  const scrollViewRef = useRef<any>(null);
  const [viewportDimensions, setViewportDimensions] = useState<{ width: number; height: number } | null>(null);
  const isInteractingWithCanvas = useRef(false);
  const scrollEnableTimeoutRef = useRef<any>(null);

  // Update weights when user data changes - FIXED: Properly load persisted settings
  // Use JSON.stringify to create a stable dependency that only changes when values actually change
  const weightsJson = useMemo(() =>
    JSON.stringify(userContextWeights),
    [userContextWeights]
  );

  React.useEffect(() => {
    if (userContextWeights && Object.keys(userContextWeights).length > 0) {
      if (__DEV__) {
        console.log('🔄 Loading persisted context weights:', userContextWeights);
      }
      setWeights({
        data: userContextWeights.data || 75,
        projects: userContextWeights.projects || 60,
        media: userContextWeights.media || 45,
        integrations: userContextWeights.integrations || 80,
        knowledge: userContextWeights.knowledge || 70
      });
    }
  }, [weightsJson]); // Use stringified version for stable comparison

  // Fetch user profile completion data for health calculation
  const { data: profileCompletionData, isLoading: profileLoading } = useQuery({
    queryKey: ['profileCompletion', userId],
    queryFn: async () => {
      if (!userId) return null;
      try {
        const response = await apis.getProfileCompletion(userId);
        return response.data;
      } catch (error) {
        console.error('Failed to fetch profile completion:', error);
        return null;
      }
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false
  });

  // UserHierarchy pattern: Track when all refs are populated to trigger positioning
  const [refsReady, setRefsReady] = useState(false);
  const [positioningComplete, setPositioningComplete] = useState(false);
  const [renderKey, setRenderKey] = useState(0); // Force re-render key
  const hasRunImperativePositioning = useRef(false); // Prevent loops
  const refsReadyRef = useRef(false); // Track if we've already set refsReady to prevent loops
  const hasRunAutoZoom = useRef(false); // Track if auto-zoom has already run to prevent loops

  // Fetch AI usage stats from the API
  const { data: aiUsageData, isLoading: aiUsageLoading } = useQuery({
    queryKey: ['aiUsageStats', userId],
    queryFn: async () => {
      if (!userId) return null;
      try {
        const response = await apis.getAIUsageStats();
        return response.data;
      } catch (error) {
        console.error('Failed to fetch AI usage stats:', error);
        return null;
      }
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    refetchOnWindowFocus: false
  });

  // AI calls data - use real data from API or fallback to defaults
  const aiCallsData = useMemo(() => ({
    today: aiUsageData?.today || 0,
    thisWeek: aiUsageData?.thisWeek || 0,
    thisMonth: aiUsageData?.thisMonth || 0,
    totalUsage: aiUsageData?.totalUsage || 0, // percentage
    limit: aiUsageData?.limit || 1000,
    breakdown: aiUsageData?.breakdown || []
  }), [aiUsageData]);

  // Calculate context health based on profile completion and context weights
  const contextHealthData = useMemo(() => {
    if (!profileCompletionData) {
      return {
        score: 50,
        status: 'Loading...',
        recommendations: ['Loading profile data...']
      };
    }

    // Calculate completion percentage
    const completionPercentage = profileCompletionData.completed
      ? 100
      : Math.max(0, Math.min(100,
        ((profileCompletionData.initialCount - profileCompletionData.emptyFieldsCount) / profileCompletionData.initialCount) * 100
      ));

    // Calculate context health based on completion percentage and weights
    const avgWeight = Object.values(weights).reduce((sum, weight) => sum + weight, 0) / Object.keys(weights).length;
    const healthScore = Math.round((completionPercentage * 0.6) + (avgWeight * 0.4));

    // Determine status and recommendations based on health score
    let status = 'Poor';
    let recommendations = [];

    if (healthScore >= 80) {
      status = 'Excellent';
      recommendations = [
        'Your profile is well-completed and context weights are optimized',
        'All systems are performing at peak efficiency',
        'Consider exploring advanced features to maximize productivity'
      ];
    } else if (healthScore >= 60) {
      status = 'Good';
      recommendations = [
        'Your profile completion is good, but could be improved',
        'Consider filling in missing profile fields for better AI assistance',
        'Context weights are well-balanced'
      ];
    } else if (healthScore >= 40) {
      status = 'Fair';
      recommendations = [
        'Profile completion needs improvement - fill in missing fields',
        'Adjust context weights to better reflect your priorities',
        'Complete at least 50% of your profile for optimal performance'
      ];
    } else {
      status = 'Poor';
      recommendations = [
        'Profile completion is low - please fill in basic information',
        'Context weights need adjustment',
        'Complete your profile to unlock full AI capabilities'
      ];
    }

    // Add specific recommendations based on completion
    if (profileCompletionData.emptyFields && profileCompletionData.emptyFields.length > 0) {
      const missingFields = profileCompletionData.emptyFields.slice(0, 3);
      recommendations.push(`Consider completing: ${missingFields.join(', ')}`);
    }

    return {
      score: healthScore,
      status,
      recommendations,
      completionPercentage: Math.round(completionPercentage),
      profileCompletion: profileCompletionData
    };
  }, [profileCompletionData, weights]);

  // Handler for context updates
  const handleContextUpdate = useCallback(async (updatedContext: any) => {


    // Refresh user data to get the updated context from the server
    try {
      await apis.refreshUser();

      // Force a re-render by updating the render key to ensure UI reflects latest data
      setTimeout(() => {
        setRenderKey(prev => prev + 1);
      }, 100);
    } catch (error) {
      console.error('❌ Failed to refresh user data:', error);
    }
  }, []);

  // Refs for UserHierarchy-style position management
  const zoomableViewRef = useRef<any>(null);
  const contextCircleRefs = useRef<Map<string, DraggableContextCircleHandle>>(new Map());
  const connectionLinesRef = useRef<any>(null);
  
  // Position refs to avoid re-renders (UserHierarchy pattern)
  const contextPositionsRef = useRef<Record<string, ContextPosition>>({});
  const userPositionRef = useRef<ContextPosition>({ x: CANVAS_SIZE.width / 2 - 100, y: CANVAS_SIZE.height / 2 - 80 });

  // Context configuration
  const contextItems = useMemo(() => [
    {
      key: 'data',
      label: 'Data',
      icon: 'storage',
      iconLibrary: 'MaterialIcons',
      color: '#3B82F6',
      gradientColors: ['#3B82F6', '#1D4ED8'],
      weight: weights.data
    },
    {
      key: 'projects',
      label: 'Projects',
      icon: 'briefcase',
      iconLibrary: 'FontAwesome5',
      color: '#10B981',
      gradientColors: ['#10B981', '#059669'],
      weight: weights.projects
    },
    {
      key: 'media',
      label: 'Media',
      icon: 'image',
      iconLibrary: 'Ionicons',
      color: '#F59E0B',
      gradientColors: ['#F59E0B', '#D97706'],
      weight: weights.media
    },
    {
      key: 'integrations',
      label: 'Integrations',
      icon: 'link',
      iconLibrary: 'MaterialIcons',
      color: '#EF4444',
      gradientColors: ['#EF4444', '#DC2626'],
      weight: weights.integrations
    },
    {
      key: 'knowledge',
      label: 'Knowledge',
      icon: 'school',
      iconLibrary: 'MaterialIcons',
      color: '#8B5CF6',
      gradientColors: ['#8B5CF6', '#7C3AED'],
      weight: weights.knowledge
    }
  ], [weights]);

  // Calculate initial positions similar to UserHierarchy (exact same pattern)
  const initialPositions = useMemo(() => {
    const centerX = CANVAS_SIZE.width / 2;
    const centerY = CANVAS_SIZE.height / 2;
    const positions: Record<string, ContextPosition> = {};

    // Place context circles in a circle around the center (like UserHierarchy)
    contextItems.forEach((item, index) => {
      const angle = (index / contextItems.length) * 2 * Math.PI;
      const radius = 350 + (item.weight / 100) * 200; // Increased radius for larger circles
      positions[item.key] = {
        x: centerX + Math.cos(angle) * radius - 110, // Offset by half of max circle size
        y: centerY + Math.sin(angle) * radius - 110
      };
    });

    return positions;
  }, [contextItems]);

  // Initialize positions on mount using refs (exact UserHierarchy pattern)
  React.useEffect(() => {
    // CRITICAL FIX: Don't overwrite existing positions, only initialize missing ones
    // This prevents drag updates from being lost when parent re-renders (UserHierarchy pattern)
    Object.entries(initialPositions).forEach(([contextKey, position]) => {
      // Only set initial position if this context doesn't already have a position
      if (!contextPositionsRef.current[contextKey]) {
        contextPositionsRef.current[contextKey] = position;
      }
    });
  }, [initialPositions]);

  // Imperative positioning: Set positions on draggable components after they mount (UserHierarchy pattern)
  React.useEffect(() => {
    const setImperativePositions = () => {
      Object.entries(initialPositions).forEach(([contextKey, position]) => {
        const componentRef = contextCircleRefs.current.get(contextKey);
        if (componentRef && !contextPositionsRef.current[contextKey]) {
          componentRef.setPosition(position);
        }
      });
    };

    // Small delay to ensure components are mounted (UserHierarchy pattern)
    const timeoutId = setTimeout(setImperativePositions, 50);
    
    return () => clearTimeout(timeoutId);
  }, [initialPositions, contextItems.length]);

  // Monitor when all component refs are ready (exact UserHierarchy pattern)
  React.useEffect(() => {
    const checkRefsReady = () => {
      const expectedContextCount = contextItems.length;
      const actualRefCount = contextCircleRefs.current.size;

      if (expectedContextCount > 0 && actualRefCount >= expectedContextCount) {
        setRefsReady(true);
      } else {
        setRefsReady(false);
      }
    };

    // Check immediately and also with a small delay (UserHierarchy pattern)
    checkRefsReady();
    const timeoutId = setTimeout(checkRefsReady, 100);
    
    return () => clearTimeout(timeoutId);
  }, [contextItems.length]);

  // Execute imperative positioning when refs are ready (exact UserHierarchy pattern)
  React.useEffect(() => {
    // PREVENT LOOPS: Only run once per component lifecycle (UserHierarchy pattern)
    if (hasRunImperativePositioning.current) {
      return;
    }

    // CRITICAL FIX: Don't run imperative positioning during drag operations (UserHierarchy pattern)
    if (isDragging) {
      return;
    }

    if (!refsReady || Object.keys(initialPositions).length === 0) {
      return;
    }

    let positionsSet = 0;
    Object.entries(initialPositions).forEach(([contextKey, position]) => {
      const componentRef = contextCircleRefs.current.get(contextKey);

      // CHANGED: Always call setPosition to force initialization, regardless of existing position (UserHierarchy pattern)
      if (componentRef) {
        componentRef.setPosition(position);
        positionsSet++;
      }
    });

    // Mark as completed to prevent re-runs (UserHierarchy pattern)
    hasRunImperativePositioning.current = true;

    if (positionsSet > 0) {
      // Force a re-render of all components by updating the render key (UserHierarchy pattern)
      setTimeout(() => {
        setRenderKey(prev => prev + 1);
        setPositioningComplete(prev => !prev);

        // Log all final component positions

      }, 100);
    }
  }, [refsReady, initialPositions, renderKey, isDragging]); // Added isDragging to deps but protected by flag

  // Center and auto-zoom the view after positioning is complete
  React.useEffect(() => {
    // Prevent running auto-zoom multiple times
    if (hasRunAutoZoom.current) {
      return;
    }

    if (!positioningComplete) {
      if (__DEV__) {
        console.log('⏳ Waiting for positioning to complete... (positioningComplete:', positioningComplete, ')');
      }
      return;
    }

    if (!zoomableViewRef.current) {
      console.warn('⚠️ Positioning complete but zoomableViewRef.current is null/undefined');
      return;
    }

    // Mark as started
    hasRunAutoZoom.current = true;

    // SIMPLE APPROACH: Just moveTo the center of all components
    const centerTimeout = setTimeout(() => {
      try {
        // Calculate bounding box center
        const userCardX = CANVAS_SIZE.width / 2 - 100;
        const userCardY = CANVAS_SIZE.height / 2 - 80;
        const userCardWidth = 200;
        const userCardHeight = 160;

        let minX = userCardX;
        let maxX = userCardX + userCardWidth;
        let minY = userCardY;
        let maxY = userCardY + userCardHeight;

        // Include all context circles
        Object.entries(contextPositionsRef.current).forEach(([key, pos]) => {
          const item = contextItems.find(i => i.key === key);
          if (!item) return;

          const baseSize = 140;
          const maxSize = 220;
          const circleSize = baseSize + (item.weight / 100) * (maxSize - baseSize);

          minX = Math.min(minX, pos.x);
          maxX = Math.max(maxX, pos.x + circleSize);
          minY = Math.min(minY, pos.y);
          maxY = Math.max(maxY, pos.y + circleSize);
        });

        // Calculate content center
        const contentCenterX = (minX + maxX) / 2;
        const contentCenterY = (minY + maxY) / 2;

        // moveTo(offsetX, offsetY) sets the pan offset directly
        // Formula: offset = (viewportCenter / zoom) - contentTarget
        const currentZoom = zoomLevel || 0.3; // Use current zoom level (default to initialZoom)
        const viewportCenterX = viewportDimensions ? viewportDimensions.width / 2 : screenDimensions.width / 2;
        const viewportCenterY = viewportDimensions ? viewportDimensions.height / 2 : screenDimensions.height / 2;

        const offsetX = (viewportCenterX / currentZoom) - contentCenterX;
        const offsetY = (viewportCenterY / currentZoom) - contentCenterY;


        // moveTo sets the offset directly
        if (zoomableViewRef.current?.moveTo) {
          zoomableViewRef.current.moveTo(offsetX, offsetY, true);

        } else {
          console.error('❌ moveTo method not available');
        }
      } catch (error) {
        console.error('❌ Error centering view:', error);
      }
    }, 500);

    return () => clearTimeout(centerTimeout);
  }, [positioningComplete, contextItems]);

  // Handle weight changes with debounced save
  const debouncedSaveWeights = useCallback(
    debounce(async (newWeights: ContextWeight) => {
      try {
        await apis.updateUserContext({ weights: newWeights });
      } catch (error) {
        console.error('❌ Failed to save context weights:', error);
      }
    }, 1000),
    []
  );

  const handleWeightChange = useCallback((key: keyof ContextWeight, value: number) => {
    const newWeights = {
      ...weights,
      [key]: value
    };

    setWeights(newWeights);
    debouncedSaveWeights(newWeights);
  }, [weights, debouncedSaveWeights]);

  // Debounced position update handler with network communication (UserHierarchy pattern)
  const debouncedPositionUpdate = useCallback(
    debounce((contextKey: string, position: ContextPosition) => {
      if (!isComponentMounted) {
        return;
      }

      // console.log('📡 Final debounced position update:', contextKey, position); // Disabled: frequent calls

      // Network communication would go here:
      // socket?.emit('commitContextTranslate', {
      //   contextKey,
      //   translateX: position.x,
      //   translateY: position.y,
      //   companyId: props.companyId
      // });
    }, 300), // Final update after 300ms of inactivity
    [isComponentMounted]
  );

  // Component lifecycle and network setup (UserHierarchy pattern)
  React.useEffect(() => {
    setIsComponentMounted(true);


    return () => {
      setIsComponentMounted(false);

      // Cancel pending debounced calls
      debouncedPositionUpdate.cancel();

      // Clear scroll enable timeout
      if (scrollEnableTimeoutRef.current) {
        clearTimeout(scrollEnableTimeoutRef.current);
      }
    };
  }, [debouncedPositionUpdate]);

  // Enhanced position change handler (exact UserHierarchy pattern - no state updates)
  const handlePositionChange = useCallback((key: string, position: ContextPosition) => {
    // Reduced logging - only log if __DEV__ to prevent performance issues

    // CRITICAL: Update position ONLY for the moved circle (no spread to prevent cascade effects) - UserHierarchy pattern
    contextPositionsRef.current[key] = position;

    // Force ConnectionLines to update (UserHierarchy pattern)
    if (connectionLinesRef.current?.forceUpdate) {
      connectionLinesRef.current.forceUpdate();
    }

    // Debounced network update (UserHierarchy pattern)
    debouncedPositionUpdate(key, position);
    
    // Don't update state to prevent re-renders - connections will update via refs (UserHierarchy pattern)
  }, [debouncedPositionUpdate]);

  // Calculate proper centering offsets like UserHierarchy
  // Memoize screen dimensions to prevent re-renders
  const screenDimensions = useMemo(() => {
    const { width, height } = Dimensions.get('window');
    return { width, height };
  }, []);

  const calculatedOffsets = useMemo(() => {
    // SIMPLE: Use (0, 0) since that's what shows components
    const offsetX = 0;
    const offsetY = 0;



    return { offsetX, offsetY };
  }, []);

  // Don't render context circles until positions are calculated (UserHierarchy pattern)
  if (Object.keys(initialPositions).length === 0) {

    return (
      <DefaultScreen backButton>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ color: theme ? '#000' : '#fff' }}>Loading positions...</Text>
        </View>
      </DefaultScreen>
    );
  }


  // User card component (fixed in center)
  const renderUserCard = () => (
    <View
      style={{
        position: 'absolute',
        left: CANVAS_SIZE.width / 2 - 100,
        top: CANVAS_SIZE.height / 2 - 80,
        width: 200,
        height: 160,
        zIndex: 100
      }}
    >
      <Card
        style={[
          getModernCardStyle(theme, 'medium'),
          {
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
          }
        ]}
      >
        <View style={{ alignItems: 'center', gap: 8 }}>
          <AvatarHandler
            avatar={userAvatar?.img || userAvatar}
            avatarImageStyle={{ width: 60, height: 60, borderRadius: 30 }}
          />
          <SizableText
            size="$4"
            fontWeight="600"
            style={{ 
              color: getModernTextColor(theme), 
              textAlign: 'center',
              pointerEvents: 'none'
            }}
          >
            {userName.fullName}
          </SizableText>
          <SizableText
            size="$2"
            style={{ 
              color: getModernTextColor(theme, 'muted'), 
              textAlign: 'center',
              pointerEvents: 'none'
            }}
          >
            {userEmail}
          </SizableText>
        </View>
      </Card>
    </View>
  );

  // Grid background component
  const GridBackground = () => {
    const gridOpacity = Math.max(0.1, Math.min(0.4, zoomLevel * 0.3 + 0.1));
    const gridColor = theme ? '#e0e0e0' : '#404040';

    return (
      <View style={StyleSheet.absoluteFillObject}>
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: CANVAS_SIZE.width,
            height: CANVAS_SIZE.height,
            backgroundColor: theme ? '#f5f5f5' : '#1a1a1a',
            opacity: 0.3
          }}
        />
        {/* Center marker */}
        <View
          style={{
            position: 'absolute',
            left: CANVAS_SIZE.width / 2 - 5,
            top: CANVAS_SIZE.height / 2 - 5,
            width: 10,
            height: 10,
            borderRadius: 5,
            backgroundColor: theme ? '#ff4444' : '#ff6666',
            opacity: 0.7
          }}
        />

        {/* Debug markers in DEV mode */}
        {__DEV__ && (
          <>
            {/* Target center position indicator */}
            <View
              style={{
                position: 'absolute',
                left: CANVAS_SIZE.width / 2 - 20,
                top: CANVAS_SIZE.height / 2 - 20,
                width: 40,
                height: 40,
                borderRadius: 20,
                borderWidth: 2,
                borderColor: '#00ff00',
                backgroundColor: 'transparent',
                opacity: 0.5
              }}
            />
            {/* Crosshair */}
            <View
              style={{
                position: 'absolute',
                left: CANVAS_SIZE.width / 2 - 1,
                top: CANVAS_SIZE.height / 2 - 50,
                width: 2,
                height: 100,
                backgroundColor: '#00ff00',
                opacity: 0.5
              }}
            />
            <View
              style={{
                position: 'absolute',
                left: CANVAS_SIZE.width / 2 - 50,
                top: CANVAS_SIZE.height / 2 - 1,
                width: 100,
                height: 2,
                backgroundColor: '#00ff00',
                opacity: 0.5
              }}
            />
            {/* Position label */}
            <View
              style={{
                position: 'absolute',
                left: CANVAS_SIZE.width / 2 + 30,
                top: CANVAS_SIZE.height / 2 - 30,
                backgroundColor: 'rgba(0,255,0,0.8)',
                padding: 4,
                borderRadius: 4
              }}
            >
              <Text style={{ color: '#000', fontSize: 10, fontWeight: 'bold' }}>
                CENTER{'\n'}
                ({CANVAS_SIZE.width / 2}, {CANVAS_SIZE.height / 2})
              </Text>
            </View>
          </>
        )}
      </View>
    );
  };

  // Render the settings section (Context Settings + Context Window Settings) - Left side
  const renderSettingsSection = () => (
    <View style={{
      flex: 1,
      minWidth: 400,
      maxWidth: Platform.OS === 'web' ? '35%' : '100%',
      marginRight: Platform.OS === 'web' ? 20 : 0,
    }}>
      <ScrollView showsVerticalScrollIndicator={false}>
        {/* Context Settings Card */}
        <ContextSettingsCard
          key={`context-card-${userContext?.updatedAt || 'initial'}`}
          theme={theme}
          onContextUpdate={handleContextUpdate}
          currentContext={userContext}
        />

        {/* Context Window Settings Card */}
        <ContextWindowSettingsCard theme={theme} />
      </ScrollView>
    </View>
  );

  // Render the header section - Right side (below canvas)
  const renderHeaderSection = () => (
    <View style={{
      width: '100%',
      marginTop: 20,
    }}>
      <HeaderComponent
        theme={theme}
        aiCallsData={aiCallsData}
        contextHealthData={contextHealthData}
      />
    </View>
  );

  const resetToZero = useCallback(() => {
    try {
      console.log('🧪 RESET ZERO: Your working moveTo(180, 200) at zoom 0.3');
      console.log('🧪 RESET ZERO: This should center on component center (1028, 974)');

      if (zoomableViewRef.current?.zoomTo) {
        zoomableViewRef.current.zoomTo(.3, true);
      }

      setTimeout(() => {
        if (zoomableViewRef.current?.moveTo) {
          console.log('🧪 RESET ZERO: Calling moveTo(180, 200)...');
          zoomableViewRef.current.moveTo(180, 200, true);

          setTimeout(() => {
            console.log('🧪 RESET ZERO: Animation complete. Pan around to trigger Transform Analysis log!');
            Alert.alert('Debug', 'Reset Zero complete!\n\nNow PAN the view slightly to see the Transform Analysis log and verify components are centered.');
          }, 1500);
        }
      }, 500);
    } catch (error) {
      console.warn('🧪 TEST: Error resetting to zero:', error);
    }
  }, []);

  // Debug controls - Center using correct offset formula
  const handleTestCenter = useCallback(() => {
    if (!zoomableViewRef.current) {
      console.warn('⚠️ Zoomable view ref not available');
      Alert.alert('Debug Error', 'Zoomable view ref not available');
      return;
    }


    try {
      // Calculate bounding box of all components
      const userCardX = CANVAS_SIZE.width / 2 - 100;
      const userCardY = CANVAS_SIZE.height / 2 - 80;
      const userCardWidth = 200;
      const userCardHeight = 160;

      let minX = userCardX;
      let maxX = userCardX + userCardWidth;
      let minY = userCardY;
      let maxY = userCardY + userCardHeight;

      Object.entries(contextPositionsRef.current).forEach(([key, pos]) => {
        const item = contextItems.find(i => i.key === key);
        if (!item) return;

        const baseSize = 140;
        const maxSize = 220;
        const circleSize = baseSize + (item.weight / 100) * (maxSize - baseSize);

        minX = Math.min(minX, pos.x);
        maxX = Math.max(maxX, pos.x + circleSize);
        minY = Math.min(minY, pos.y);
        maxY = Math.max(maxY, pos.y + circleSize);
      });

      // Calculate component center
      const contentCenterX = (minX + maxX) / 2;
      const contentCenterY = (minY + maxY) / 2;

      // Calculate offset using correct formula: offset = (viewportCenter / zoom) - contentTarget
      const currentZoom = zoomLevel || 0.3;
      const viewportCenterX = viewportDimensions ? viewportDimensions.width / 2 : screenDimensions.width / 2;
      const viewportCenterY = viewportDimensions ? viewportDimensions.height / 2 : screenDimensions.height / 2;

      const offsetX = (viewportCenterX / currentZoom) - contentCenterX;
      const offsetY = (viewportCenterY / currentZoom) - contentCenterY;

      // moveTo sets the offset directly
      if (zoomableViewRef.current?.moveTo) {
        zoomableViewRef.current.moveTo(offsetX, offsetY, true);
        Alert.alert('Debug', `Centered on components!\nContent: (${contentCenterX.toFixed(0)}, ${contentCenterY.toFixed(0)})\nOffset: (${offsetX.toFixed(0)}, ${offsetY.toFixed(0)})`);
      } else {
        console.error('❌ TEST: moveTo not available');
        Alert.alert('Error', 'moveTo not available');
      }
    } catch (error) {
      console.error('🧪 TEST: Error:', error);
      Alert.alert('Debug Error', `Error: ${error.message}`);
    }
  }, [contextItems, zoomLevel, viewportDimensions, screenDimensions]);

  const handleTestZoomIn = useCallback(async () => {
    if (zoomableViewRef.current?.zoomBy) {
      try {
        await zoomableViewRef.current.zoomBy(0.2, true);
      } catch (error) {
        console.error('🧪 TEST: Zoom in error:', error);
      }
    }
  }, []);

  const handleTestZoomOut = useCallback(async () => {
    if (zoomableViewRef.current?.zoomBy) {
      try {
        await zoomableViewRef.current.zoomBy(-0.2, true);
      } catch (error) {
        console.error('🧪 TEST: Zoom out error:', error);
      }
    }
  }, []);

  const handleTestReset = useCallback(() => {
    if (!zoomableViewRef.current) {
      console.warn('⚠️ Zoomable view ref not available');
      Alert.alert('Debug Error', 'Zoomable view ref not available');
      return;
    }


    try {
      // Calculate component center (same as in auto-center logic)
      const userCardX = CANVAS_SIZE.width / 2 - 100;
      const userCardY = CANVAS_SIZE.height / 2 - 80;
      const userCardWidth = 200;
      const userCardHeight = 160;

      let minX = userCardX;
      let maxX = userCardX + userCardWidth;
      let minY = userCardY;
      let maxY = userCardY + userCardHeight;

      Object.entries(contextPositionsRef.current).forEach(([key, pos]) => {
        const item = contextItems.find(i => i.key === key);
        if (!item) return;

        const baseSize = 140;
        const maxSize = 220;
        const circleSize = baseSize + (item.weight / 100) * (maxSize - baseSize);

        minX = Math.min(minX, pos.x);
        maxX = Math.max(maxX, pos.x + circleSize);
        minY = Math.min(minY, pos.y);
        maxY = Math.max(maxY, pos.y + circleSize);
      });

      const componentCenterX = (minX + maxX) / 2;
      const componentCenterY = (minY + maxY) / 2;


      // Test: Call moveTo with actual component center content coordinates
      if (zoomableViewRef.current?.moveTo) {
        zoomableViewRef.current.moveTo(componentCenterX, componentCenterY, true);


        Alert.alert('Debug', `Testing moveTo with component center!\nPosition: (${componentCenterX.toFixed(0)}, ${componentCenterY.toFixed(0)})\n\nWait 1 second, then check if centered.`);
      }
    } catch (error) {
      console.error('🧪 TEST: Error:', error);
      Alert.alert('Debug Error', `Error: ${error.message}`);
    }
  }, [contextItems]);

  // Render the interactive canvas
  const renderCanvas = () => (
    <ErrorBoundary>
      <View
        style={{
          flex: Platform.OS === 'web' ? 1 : undefined,
          width: Platform.OS === 'web' ? '100%' : screenWidth - 20, // Full width on web (within right column), minus margins on mobile
          borderRadius: 20,
          alignSelf: Platform.OS === 'web' ? 'flex-start' : 'center',
          height: Platform.OS === 'web' ? undefined : screenHeight * 0.5,
          minHeight: Platform.OS === 'web' ? 600 : screenHeight * 0.4,
          maxHeight: screenHeight * 0.6, // 40vh equivalent
          minWidth: Platform.OS === 'web' ? 500 : undefined,
          maxWidth: Platform.OS === 'web' ? '100%' : screenWidth - 20,
          margin: Platform.OS === 'web' ? 0 : 10,
          marginBottom: Platform.OS === 'web' ? 20 : 20,
          ...(theme ? styles.card : styles.infoCardDark),
          padding: 0,
          overflow: 'hidden'
        }}
      >
        <GestureHandlerRootView style={{ flex: 1 }}>
          <ReactNativeZoomableView
            ref={zoomableViewRef}
            maxZoom={2}
            minZoom={0.3}
        initialZoom={Platform.select({ default: 0.7, native: 0.3 })}
            contentWidth={CANVAS_SIZE.width}
            contentHeight={CANVAS_SIZE.height}
        initialOffsetX={calculatedOffsets.offsetX}
        initialOffsetY={calculatedOffsets.offsetY}
            panBoundaryPadding={200}
        onLayout={(event) => {
          const { width, height } = event.nativeEvent.layout;
          setViewportDimensions({ width, height });
          if (__DEV__) {
            console.log('📐 ZoomableView viewport size:', { width, height });
          }
        }}
            onStartShouldSetPanResponder={Platform.OS === 'android' ? undefined : (evt, gestureState) => {
          // Let the zoomable view handle its own gestures, but disable scroll
          if (Platform.OS !== 'web') {
            if (scrollEnableTimeoutRef.current) {
              clearTimeout(scrollEnableTimeoutRef.current);
            }
            isInteractingWithCanvas.current = true;
            setScrollEnabled(false);
          }
          return true;
        }}
            onPanResponderGrant={Platform.OS === 'android' ? undefined : (evt, gestureState) => {
          // Mark as actively interacting to prevent scroll
          if (Platform.OS !== 'web') {
            isInteractingWithCanvas.current = true;
            setScrollEnabled(false);
          }
        }}
            onPanResponderTerminationRequest={Platform.OS === 'android' ? undefined : () => {
          // Don't allow termination while we're zooming/panning
          return false;
        }}
        onTransform={(event) => {
          setZoomLevel(event.zoomLevel);

          // While transforming, keep scroll disabled
          if (Platform.OS !== 'web') {
            isInteractingWithCanvas.current = true;
            setScrollEnabled(false);

            // Clear any pending timeout
            if (scrollEnableTimeoutRef.current) {
              clearTimeout(scrollEnableTimeoutRef.current);
            }

            // Set a timeout to re-enable scroll after interaction stops
            scrollEnableTimeoutRef.current = setTimeout(() => {
              isInteractingWithCanvas.current = false;
              setScrollEnabled(true);
            }, 300); // Re-enable after 300ms of no transform events
          }

          if (__DEV__) {
            // Throttle logging to every 2 seconds to avoid spam
            const now = Date.now();
            if (!(global as any).__lastTransformLog || now - (global as any).__lastTransformLog > 2000) {
              (global as any).__lastTransformLog = now;

              // event.offsetX/Y = current pan offset (how much the content has been translated)
              // event.originalWidth/Height = viewport dimensions
              // event.originalPageX/Y = absolute position of viewport

              const viewportCenterX = event.originalWidth / 2;
              const viewportCenterY = event.originalHeight / 2;

              // What content coordinate is at the viewport center?
              // Formula: contentAtCenter = (viewportCenter / zoom) - offset
              const contentAtCenterX = (viewportCenterX / event.zoomLevel) - event.offsetX;
              const contentAtCenterY = (viewportCenterY / event.zoomLevel) - event.offsetY;

              // Reverse engineer: if we want to center at content(1028, 974), what moveTo values?
              // moveTo might set offset directly, so: offset = (viewportCenter / zoom) - contentTarget
              const moveToX = (viewportCenterX / event.zoomLevel) - 1028;
              const moveToY = (viewportCenterY / event.zoomLevel) - 974;

            }
          }
        }}
            style={{ flex: 1, borderRadius: 16 }}
          >
            <View style={{
              width: CANVAS_SIZE.width,
              height: CANVAS_SIZE.height,
              backgroundColor: 'transparent',
              ...(Platform.OS === 'android' && {
                // Android-specific optimizations to prevent crashes
                collapsable: false,
                needsOffscreenAlphaCompositing: false,
              })
            }}>
              {/* Background */}
              <GridBackground />

              {/* Connection Lines */}
              <ConnectionLines
                ref={connectionLinesRef}
                userPosition={userPositionRef.current}
                contextPositionsRef={contextPositionsRef}
                contextItems={contextItems}
                theme={theme}
                zoomLevel={zoomLevel}
              />

              {/* User Card (Fixed in center) */}
              {renderUserCard()}

          {/* Debug: Component counter in DEV mode */}
          {__DEV__ && (
            <View style={{
              position: 'absolute',
              top: 50,
              left: 50,
              backgroundColor: 'rgba(0,0,0,0.8)',
              padding: 10,
              borderRadius: 8,
              zIndex: 9999
            }}>
              <Text style={{ color: 'white', fontSize: 12, fontWeight: 'bold' }}>
                🎨 {contextItems.length} circles rendering
              </Text>
              <Text style={{ color: 'white', fontSize: 10 }}>
                Refs: {contextCircleRefs.current.size}/{contextItems.length}
              </Text>
              <Text style={{ color: 'white', fontSize: 10 }}>
                Zoom: {zoomLevel.toFixed(2)}
              </Text>
            </View>
          )}

              {/* Draggable Context Circles - UserHierarchy pattern with render key */}
              {contextItems.map((item) => {
                // Get position from ref first, then from calculated positions (UserHierarchy pattern)
                const refPosition = contextPositionsRef.current[item.key];
                const calculatedPosition = initialPositions[item.key];
                
                // Use calculated position, or fallback to center if not ready yet (UserHierarchy pattern)
                const initialPosition = refPosition || calculatedPosition || {
                  x: CANVAS_SIZE.width / 2,
                  y: CANVAS_SIZE.height / 2
                };

                return (
                  <DraggableContextCircle
                    key={`${item.key}-${renderKey}`} // Force re-render with key (UserHierarchy pattern)
                    ref={(ref) => {
                      if (ref) {
                        contextCircleRefs.current.set(item.key, ref);
                        
                        // Check if all refs are now ready AND we haven't already triggered positioning
                        if (contextCircleRefs.current.size >= contextItems.length && !refsReadyRef.current) {
                          // Use setTimeout to ensure this happens after render
                          setTimeout(() => {
                            // Double-check flag hasn't been set during timeout
                            if (!refsReadyRef.current) {
                              refsReadyRef.current = true; // Mark as set to prevent future calls
                              setRefsReady(true);
                            }
                          }, 10);
                        }
                      } else {
                        contextCircleRefs.current.delete(item.key);
                        // Reset flag when refs are removed so we can re-initialize on re-mount
                        if (contextCircleRefs.current.size === 0) {
                          refsReadyRef.current = false;
                        }
                      }
                    }}
                    item={item}
                    initialPosition={initialPosition}
                    onPositionChange={handlePositionChange}
                    theme={theme}
                    zoomLevel={zoomLevel}
                    isDragging={isDragging}
                    setIsDragging={setIsDragging}
                    zoomableViewRef={zoomableViewRef}
                  />
                );
              })}
            </View>
          </ReactNativeZoomableView>
        </GestureHandlerRootView>

        {/* Context Controls - Only on mobile */}

          <View style={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            right: 8,
            backgroundColor: theme ? 'rgba(255,255,255,0.95)' : 'rgba(30,30,30,0.95)',
            borderRadius: 12,
            padding: 12,
            borderWidth: 1,
            borderColor: theme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.2)',
            zIndex: 9999
          }}>
            <Text style={{
              fontSize: 12,
              fontWeight: 'bold',
              color: theme ? '#1e293b' : '#f1f5f9',
              marginBottom: 8,
              textAlign: 'center'
            }}>
              Context Controls
            </Text>

            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              <Pressable
                onPress={handleTestCenter}
                style={{
                  backgroundColor: theme ? '#3B82F6' : '#1E40AF',
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  borderRadius: 8,
                  minWidth: 80,
                  alignItems: 'center'
                }}
              >
                <Text style={{ color: 'white', fontSize: 11, fontWeight: '600' }}>
                  Center
                </Text>
              </Pressable>

              <Pressable
                onPress={handleTestZoomIn}
                style={{
                  backgroundColor: theme ? '#10B981' : '#065F46',
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  borderRadius: 8,
                  minWidth: 60,
                  alignItems: 'center'
                }}
              >
                <Text style={{ color: 'white', fontSize: 11, fontWeight: '600' }}>
                  Zoom In
                </Text>
              </Pressable>

              <Pressable
                onPress={handleTestZoomOut}
                style={{
                  backgroundColor: theme ? '#F59E0B' : '#92400E',
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  borderRadius: 8,
                  minWidth: 60,
                  alignItems: 'center'
                }}
              >
                <Text style={{ color: 'white', fontSize: 11, fontWeight: '600' }}>
                  Zoom Out
                </Text>
              </Pressable>
            </View>

            {/* Zoom level indicator */}
            <View style={{ marginTop: 8, alignItems: 'center' }}>
              <Text style={{ fontSize: 10, color: theme ? '#64748b' : '#94a3b8' }}>
                Zoom: {zoomLevel.toFixed(2)}x
              </Text>
          </View>
        </View>
      </View>
    </ErrorBoundary>
  );

  return (
    <DefaultScreen backButton style={{ flex: 1, paddingBottom: 50 }} containerStyle={{ paddingBottom: 50, paddingTop: 0 }}>
      <ErrorBoundary>
      {Platform.OS === 'web' ? (
          // Web Layout - Settings on left, Canvas + Header on right
          <XStack style={{ flex: 1, width: '100%', alignItems: 'flex-start' }} gap="$4">
            {/* Left side - Settings */}
            {renderSettingsSection()}

            {/* Right side - Canvas + Header */}
            <YStack style={{ flex: 1, minWidth: 500 }}>
              {renderCanvas()}
              {renderHeaderSection()}
            </YStack>

            {/* Floating Slider Controls - Fixed position outside the card */}
            <FloatingSliderControls
              contextItems={contextItems}
              weights={weights}
              onWeightChange={handleWeightChange}
              theme={theme}
            />
          </XStack>
      ) : (
        // Mobile Layout - Single vertical ScrollView
        <ScrollView
          ref={scrollViewRef}
          style={{ flex: 1, width: '100%' }}
          showsVerticalScrollIndicator={false}
          scrollEnabled={scrollEnabled}
          contentContainerStyle={{
            paddingBottom: 120,
            width: '100%',
            alignItems: 'stretch'
          }}
        >
          {/* Canvas first on mobile */}
          {renderCanvas()}
          <FloatingSliderControls
            contextItems={contextItems}
            weights={weights}
            onWeightChange={handleWeightChange}
            theme={theme}
          />
              {/* Context Intelligence section below canvas */}
          <View style={{ marginHorizontal: Platform.select({ native: -20 }), width: Platform.select({ web: '100%', native: '110%' }), paddingHorizontal: 0 }}>
            {/* Header */}
            <HeaderComponent
              theme={theme}
              aiCallsData={aiCallsData}
              contextHealthData={contextHealthData}
            />

            {/* Context Settings Card */}
            <ContextSettingsCard
              key={`context-card-${userContext?.updatedAt || 'initial'}`}
              theme={theme}
              onContextUpdate={handleContextUpdate}
              currentContext={userContext}
            />

                {/* Context Window Settings Card */}
                <ContextWindowSettingsCard theme={theme} />
          </View>
        </ScrollView>
      )}
      </ErrorBoundary>
    </DefaultScreen>
  );
};

export default ContextScreen;
